/* eslint-disable complexity */
/**
 * Generates an image for Instagram sharing using HTML5 Canvas.
 * Matches style: Title Top, Hero Middle, Footer with Branding/Date.
 * @param {string} authorImageUrl - The URL of the author's headshot.
 * @param {string} authorName - The author's name.
 * @param {string} byLabel - The translated "By" label.
 * @param {string} dateString - The formatted date string.
 * @param {string} heroImageUrl - The URL of the hero image.
 * @param {string} logoUrl - The URL of the brand logo.
 * @param {string} title - The title of the blog post.
 * @returns {Promise<string>} - A promise that resolves to the data URL of the generated image.
 */
// eslint-disable-next-line max-params
export async function generateInstagramShareImage(
	authorImageUrl,
	authorName,
	byLabel,
	dateString,
	heroImageUrl,
	logoUrl,
	title
) {
	const canvas = document.createElement('canvas');
	const ctx = canvas.getContext('2d');

	if (!ctx) {
		return '';
	}

	// Instagram Portrait 4:5
	const width = 1080;
	const height = 1350;
	const padding = 60;
	const borderRadius = 40; // Rounded corners for the card look

	canvas.width = width;
	canvas.height = height;

	// 0. Setup Card Shape (Clip & Fill)
	ctx.beginPath();
	ctx.moveTo(borderRadius, 0);
	ctx.lineTo(width - borderRadius, 0);
	ctx.quadraticCurveTo(width, 0, width, borderRadius);
	ctx.lineTo(width, height - borderRadius);
	ctx.quadraticCurveTo(width, height, width - borderRadius, height);
	ctx.lineTo(borderRadius, height);
	ctx.quadraticCurveTo(0, height, 0, height - borderRadius);
	ctx.lineTo(0, borderRadius);
	ctx.quadraticCurveTo(0, 0, borderRadius, 0);
	ctx.closePath();

	// Clip content to rounded rect
	ctx.clip();
	// Fill Background White
	ctx.fillStyle = '#ffffff';
	ctx.fillRect(0, 0, width, height);

	// Load Images
	/**
	 * @param {string} url
	 * @returns {Promise<any>}
	 */
	function loadImage(url) {
		return new Promise((resolve) => {
			const img = new Image();
			img.crossOrigin = 'Anonymous';

			img.onload = () => {
				resolve(img);
			};

			img.onerror = () => {
				resolve(null);
			};

			img.src = url;
		});
	}

	try {
		const [heroImg, logoImg, authorImg] = await Promise.all([
			loadImage(heroImageUrl),
			loadImage(logoUrl),
			loadImage(authorImageUrl),
		]);

		/*
		 * ... (HEADER & BODY code same as before, skipping for brevity but needs full content if replacement is large chunk)
		 * Re-implementing full function body replacement to ensure context is correct
		 */

		// --- 1. HEADER: TITLE ---

		ctx.save();
		ctx.fillStyle = '#0a0a0a';
		ctx.font = 'bold 72px Arial, sans-serif';
		ctx.textAlign = 'left';
		ctx.textBaseline = 'top';

		// eslint-disable-next-line prettier/prettier
		const maxWidth = width - (padding * 2);
		const words = title ? title.split(' ') : [];
		let line = '';
		let lines = [];
		const lineHeight = 86;

		for (let wordIndex = 0; wordIndex < words.length; wordIndex += 1) {
			const testLine = `${line}${words[wordIndex]} `;
			const metrics = ctx.measureText(testLine);

			if (metrics.width > maxWidth && wordIndex > 0) {
				lines.push(line);
				line = `${words[wordIndex]} `;
			} else {
				line = testLine;
			}
		}
		lines.push(line);

		if (lines.length > 3) {
			lines = lines.slice(0, 3);
			lines[2] = `${lines[2]}...`;
		}

		let currentY = padding + 20;
		lines.forEach((singleLine) => {
			ctx.fillText(singleLine.trim(), padding, currentY);
			currentY += lineHeight;
		});
		currentY += 30;

		ctx.restore();

		// --- 2. FOOTER Calculation ---
		const footerHeight = 200;
		const footerY = height - footerHeight;

		// --- 3. BODY: HERO IMAGE ---
		const imageStartY = currentY;
		const imageEndY = footerY;
		const imageMaxHeight = imageEndY - imageStartY;

		if (heroImg && imageMaxHeight > 0) {
			const imgRatio = heroImg.width / heroImg.height;
			const targetRatio = width / imageMaxHeight;

			let sWidth;
			let sHeight;
			let sx;
			let sy;
			if (imgRatio > targetRatio) {
				sHeight = heroImg.height;
				sWidth = sHeight * targetRatio;
				sx = (heroImg.width - sWidth) / 2;
				sy = 0;
			} else {
				sWidth = heroImg.width;
				sHeight = sWidth / targetRatio;
				sx = 0;
				sy = (heroImg.height - sHeight) / 2;
			}
			ctx.drawImage(
				heroImg,
				sx,
				sy,
				sWidth,
				sHeight,
				0,
				imageStartY,
				width,
				imageMaxHeight
			);
		} else {
			// eslint-disable-next-line require-atomic-updates
			ctx.fillStyle = '#f0f0f0';
			ctx.fillRect(0, imageStartY, width, imageMaxHeight);
		}

		// --- 4. FOOTER: CONTENT ---
		ctx.beginPath();
		ctx.moveTo(padding, footerY);
		ctx.lineTo(width - padding, footerY);
		ctx.strokeStyle = '#e5e7eb';
		ctx.lineWidth = 3;
		ctx.stroke();

		// eslint-disable-next-line prettier/prettier
		const footerContentY = footerY + (footerHeight / 2);

		// 1. AUTHOR (Left)
		let authorEndX = padding;

		if (authorImg) {
			const authorImgSize = 70; // Reduced from 90
			// eslint-disable-next-line prettier/prettier
			const authorImgY = footerContentY - (authorImgSize / 2);

			ctx.save();
			ctx.beginPath();
			ctx.arc(
				// eslint-disable-next-line prettier/prettier
				padding + (authorImgSize / 2),
				footerContentY,
				authorImgSize / 2,
				0,
				Math.PI * 2,
				true
			);
			ctx.closePath();
			ctx.clip();
			ctx.drawImage(
				authorImg,
				padding,
				authorImgY,
				authorImgSize,
				authorImgSize
			);
			ctx.restore();

			authorEndX += authorImgSize + 15; // Slightly reduced padding
		}

		ctx.font = 'bold 26px Arial, sans-serif'; // Reduced from 32px
		ctx.fillStyle = '#1f2937';
		ctx.textAlign = 'left';
		ctx.textBaseline = 'middle';

		const byText = `${byLabel || ''} ${
			authorName ? authorName.toUpperCase() : ''
		}`;
		ctx.fillText(byText, authorEndX, footerContentY);

		// Measure text to find true end
		const authorMetrics = ctx.measureText(byText);
		authorEndX += authorMetrics.width;

		// 2. DATE (Right) - Pre-calculate to help logo positioning
		let dateStartX = width - padding;
		if (dateString) {
			ctx.font = '26px Arial, sans-serif'; // Reduced from 32px
			ctx.fillStyle = '#6b7280';
			ctx.textAlign = 'right';
			ctx.textBaseline = 'middle';
			// We don't draw yet, just measure
			const dateMetrics = ctx.measureText(dateString.toUpperCase());
			dateStartX -= dateMetrics.width;
		}

		// 3. LOGO (Dynamic Center)
		if (logoImg) {
			const logoSize = 180; // Increased from 140
			const logoRatio = logoImg.width / logoImg.height;
			let drawLogoW = logoSize;
			let drawLogoH = logoSize;

			if (logoRatio > 1) {
				drawLogoH = logoSize / logoRatio;
			} else {
				drawLogoW = logoSize * logoRatio;
			}

			// Calculate "Visual Center" between Author End and Date Start

			const availableStart = authorEndX + 20;
			const availableEnd = dateStartX - 20;

			// Ideal Center: Canvas Center
			const logoCenterX = width / 2;

			// Constraint Check:
			// eslint-disable-next-line prettier/prettier
			let logoX = logoCenterX - (drawLogoW / 2);

			// If overlapping left (Author), push right
			if (logoX < availableStart) {
				logoX = availableStart;
			}

			// If overlapping right (Date), push left
			if (logoX + drawLogoW > availableEnd) {
				// Center it in whatever tiny space exists
				const midPoint = (availableStart + availableEnd) / 2;
				// eslint-disable-next-line prettier/prettier
				logoX = midPoint - (drawLogoW / 2);
			}

			// eslint-disable-next-line prettier/prettier
			const logoY = footerContentY - (drawLogoH / 2);

			ctx.drawImage(logoImg, logoX, logoY, drawLogoW, drawLogoH);
		}

		// Draw Date (Now actually draw it)
		if (dateString) {
			ctx.font = '26px Arial, sans-serif'; // Match reduced size
			ctx.fillStyle = '#6b7280';
			ctx.textAlign = 'right';
			ctx.textBaseline = 'middle';
			ctx.fillText(
				dateString.toUpperCase(),
				width - padding,
				footerContentY
			);
		}

		/*
		 * --- 5. BORDER STROKE (Overlay) ---
		 * Re-trace path for stroke
		 */
		ctx.beginPath();
		ctx.moveTo(borderRadius, 0);
		ctx.lineTo(width - borderRadius, 0);
		ctx.quadraticCurveTo(width, 0, width, borderRadius);
		ctx.lineTo(width, height - borderRadius);
		ctx.quadraticCurveTo(width, height, width - borderRadius, height);
		ctx.lineTo(borderRadius, height);
		ctx.quadraticCurveTo(0, height, 0, height - borderRadius);
		ctx.lineTo(0, borderRadius);
		ctx.quadraticCurveTo(0, 0, borderRadius, 0);
		ctx.closePath();

		ctx.strokeStyle = '#d1d5db'; // Tailwind gray-300
		ctx.lineWidth = 4; // Visible border
		ctx.stroke();
	} catch (error) {
		// console.error('Error generating Instagram image:', error);
	}

	return canvas.toDataURL('image/png');
}
